import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, AlertTriangle, Check } from 'lucide-react';
import { instances, type InstanceImage } from '../api';

/**
 * Bildtags einer Instanz — je Dienst einer.
 *
 * Der Zweck ist der Rückweg. Wer etwas ausprobiert und merkt, dass es kaputt
 * ist, will das Image von gestern zurück, an dieser einen Instanz und ohne das
 * Release umzuschreiben — denn das zöge alle anderen Instanzen mit.
 *
 * Deshalb steht neben jedem Tag, woher er kommt, und deshalb ist
 * „Zurück aufs Release" ein eigener Knopf: ein Weg hinein ohne sichtbaren Weg
 * heraus wäre eine Falle.
 */

function Herkunftsmarke({ bild }: { bild: InstanceImage }) {
  const { t } = useTranslation();
  const farben: Record<InstanceImage['herkunft'], string> = {
    instanz: 'bg-amber-50 text-amber-700 ring-amber-200',
    release: 'bg-sky-50 text-sky-700 ring-sky-200',
    standard: 'bg-slate-100 text-slate-600 ring-slate-200',
    // Rot, und das ist hier richtig: die Vorlage hält den Deploy an. Das ist
    // keine Einstellung, sondern etwas, das jemand tun muss.
    offen: 'bg-red-50 text-red-700 ring-red-200',
  };
  const namen: Record<InstanceImage['herkunft'], string> = {
    instanz: t('images.fromInstance'),
    release: t('images.fromRelease'),
    standard: t('images.fromDefault'),
    offen: t('images.fromOpen'),
  };
  return (
    <span
      title={bild.herkunft === 'offen' ? t('images.openHint') : undefined}
      className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ${farben[bild.herkunft]}`}
    >
      {namen[bild.herkunft]}
    </span>
  );
}

function Zeile({
  instanceId,
  bild,
  onGespeichert,
}: {
  instanceId: string;
  bild: InstanceImage;
  onGespeichert: () => void;
}) {
  const { t } = useTranslation();
  const [eigen, setEigen] = useState('');

  // Erst laden, wenn jemand die Liste aufklappt. Jeder Tag kostet die Registry
  // zwei Anfragen; das beim Öffnen der Instanzseite für drei Repositories zu
  // tun, hiesse dreimal zu bezahlen für etwas, das meistens niemand anschaut.
  const [offen, setOffen] = useState(false);
  const tagListe = useQuery({
    queryKey: ['instance-image-tags', instanceId, bild.repository],
    queryFn: () => instances.imageTags(instanceId, bild.repository),
    enabled: offen,
    staleTime: 60_000,
  });

  const setzen = useMutation({
    mutationFn: (tag: string | null) => instances.setImageTag(instanceId, bild.schluessel, tag),
    onSuccess: onGespeichert,
  });

  const laeuft = setzen.isPending;

  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="py-2 pr-3">
        <div className="font-medium text-slate-700">{bild.dienst}</div>
        <div className="font-mono text-[11px] text-slate-400">{bild.repository}</div>
      </td>
      <td className="py-2 pr-3">
        <select
          value={bild.instanzTag ?? ''}
          disabled={laeuft}
          onFocus={() => setOffen(true)}
          onChange={(e) => {
            if (e.target.value === '__eigen__') return;
            setzen.mutate(e.target.value || null);
          }}
          className="w-full rounded border border-slate-300 px-1.5 py-1 font-mono text-xs disabled:opacity-50"
        >
          {/* Der leere Eintrag ist der Rueckweg, und er steht oben. */}
          <option value="">
            {bild.releaseTag
              ? `— ${t('images.fromRelease')}: ${bild.releaseTag}`
              : bild.standard
                ? `— ${t('images.fromDefault')}: ${bild.standard}`
                : `— ${t('images.fromOpen')}`}
          </option>
          {tagListe.data?.tags.map((x) => (
            <option key={x.tag} value={x.tag}>
              {x.tag}
              {x.createdAt ? `  ·  ${t('images.built')} ${x.createdAt.slice(0, 10)}` : ''}
            </option>
          ))}
          {/* Ein von Hand gesetzter Tag, den die Registry nicht mehr fuehrt,
              darf nicht stillschweigend aus der Auswahl fallen -- sonst zeigt
              das Feld etwas anderes an, als gilt. */}
          {bild.instanzTag && !tagListe.data?.tags.some((x) => x.tag === bild.instanzTag) && (
            <option value={bild.instanzTag}>{bild.instanzTag}</option>
          )}
        </select>

        {tagListe.data?.grund && (
          <div className="mt-1 flex items-start gap-1 text-[11px] text-amber-700">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>{t('images.registryFailed')}</span>
          </div>
        )}

        {/* Eintippen — fuer den Fall, dass die Registry schweigt oder der Tag
            aelter ist als das, was die Liste zeigt. */}
        <div className="mt-1 flex gap-1">
          <input
            value={eigen}
            onChange={(e) => setEigen(e.target.value)}
            placeholder={t('images.custom')}
            disabled={laeuft}
            className="w-full rounded border border-slate-200 px-1.5 py-0.5 font-mono text-[11px]"
          />
          <button
            type="button"
            disabled={!eigen.trim() || laeuft}
            onClick={() => {
              setzen.mutate(eigen.trim());
              setEigen('');
            }}
            className="rounded border border-slate-300 px-1.5 text-[11px] text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            <Check size={12} />
          </button>
        </div>
      </td>
      <td className="py-2 pr-3">
        <div className="font-mono text-xs text-slate-700">{bild.wirksam ?? '—'}</div>
        <div className="mt-1">
          <Herkunftsmarke bild={bild} />
        </div>
      </td>
      <td className="py-2 text-right">
        {bild.instanzTag && (
          <button
            type="button"
            title={t('images.resetHint')}
            disabled={laeuft}
            onClick={() => setzen.mutate(null)}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            <RotateCcw size={12} />
            {t('images.reset')}
          </button>
        )}
      </td>
    </tr>
  );
}

export default function BildtagPanel({ instanceId }: { instanceId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [gespeichert, setGespeichert] = useState(false);

  const { data: bilder, isLoading } = useQuery({
    queryKey: ['instance-images', instanceId],
    queryFn: () => instances.images(instanceId),
  });

  if (isLoading) return null;
  // Kein eigenes Image heisst: hier gibt es nichts zu setzen. Ein leerer Kasten
  // waere eine Frage ohne Antwort.
  if (!bilder?.length) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-surface p-4">
      <h3 className="text-sm font-semibold text-slate-700">{t('images.title')}</h3>
      <p className="mt-0.5 text-xs text-slate-500">{t('images.intro')}</p>

      {gespeichert && (
        <div className="mt-2 rounded bg-emerald-50 px-2 py-1 text-xs text-emerald-700">
          {t('images.deployNeeded')}
        </div>
      )}

      <table className="mt-3 w-full text-xs">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
            <th className="pb-1 pr-3 font-medium">{t('images.service')}</th>
            <th className="pb-1 pr-3 font-medium">{t('images.pick')}</th>
            <th className="pb-1 pr-3 font-medium">{t('images.tag')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {bilder.map((b) => (
            <Zeile
              key={b.schluessel}
              instanceId={instanceId}
              bild={b}
              onGespeichert={() => {
                qc.invalidateQueries({ queryKey: ['instance-images', instanceId] });
                setGespeichert(true);
              }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
